import React, { Dispatch, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { Alert, Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import getLoginUser, { User } from 'core/application/account/loginUser'
import { updateUserInStore } from 'core/application/account/updateUserInStore/updateUserInStore'
import { FamilyStatus as FamilyStatusEnum, FamilyStatusValues } from 'core/application/employeeSignup/familyStatus'

import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import notification from 'common/components/feedback/Notification'
import CategoryItemPageHeader from 'common/components/onBoarding/Common'

import { commandRequest } from 'common/RequestUtils'

import { BaseComponentProps } from '../models'

const formValidator = Yup.object({
  familyStatusId: Yup.number().nullable().required('Please enter an family status.'),
})

export interface FamilyStatusProps {
  familyStatusValues: FamilyStatusValues
  familyStatusApi: (familyStatus: FamilyStatusValues) => Promise<any>
  numberOfQuestions: number
  currentQuestion: number
  finish: boolean
}

const FamilyStatus: React.FC<BaseComponentProps<FamilyStatusProps>> = (props) => {
  const { bindSubmitFormHandler } = props
  const { t: translate } = useTranslation()

  useEffect(() => {
    props.setIsFormValid(formValidator.isValidSync(props.state))
  }, [])

  const onSubmit = async (values: FamilyStatusValues) => {
    const response = await commandRequest(() => props.state.familyStatusApi(values))
    if (response.errors) {
      props.setIsSubmiting(false)
      notification.open({
        message: response.errors[0],
        type: 'error',
      })
    } else {
      if (props.state.finish) {
        await updateUserInLocalStore()
        props.finish()
      } else props.goNext()
    }
  }

  const updateUserInLocalStore = async () => {
    try {
      const user: User = await getLoginUser()
      updateUserInStore(user)
    } catch (err) {
      notification.open(err as any)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator}
      initialValues={props.state.familyStatusValues}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
        props.setIsFormValid(formValidator.isValidSync(values))
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form onSubmit={handleSubmit} autoComplete="off">
            <Row justify={window.innerWidth < 767 ? 'center' : 'start'} className="mt-32">
              <Col xs={23} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <CategoryItemPageHeader
                  title={translate('ee-signup.family-status.header')}
                  numberOfQuestions={props.state.numberOfQuestions}
                  currentQuestion={props.state.currentQuestion}
                  question={translate('ee-signup.family-status.question')}
                />
              </Col>
            </Row>

            <Row gutter={16} justify={window.innerWidth < 767 ? 'center' : 'start'}>
              <Col xs={23} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }} className="pb-16">
                <Alert type="info" showIcon description={translate('ee-signup.family-status.info')} />
              </Col>

              <Col xs={23} sm={23} md={{ span: 15, offset: 2 }} lg={{ span: 12, offset: 2 }}>
                <Radio.Group name="familyStatusId" defaultValue={props.state.familyStatusValues.familyStatusId}>
                  <Row gutter={16}>
                    <Col sm={24} xs={24} md={24} lg={12} xl={12} className="pb-16">
                      <Radio.Button value={FamilyStatusEnum.Single}>
                        {translate('ee-signup.family-status.single')}
                      </Radio.Button>
                    </Col>

                    <Col sm={24} xs={24} md={24} lg={12} xl={12} className="pb-16">
                      <Radio.Button value={FamilyStatusEnum.SingleWithDependents}>
                        {translate('ee-signup.family-status.single-with-dependents')}
                      </Radio.Button>
                    </Col>
                    <Col sm={24} xs={24} md={24} lg={12} xl={12} className="pb-16">
                      <Radio.Button value={FamilyStatusEnum.Married}>
                        {translate('ee-signup.family-status.married')}
                      </Radio.Button>
                    </Col>

                    <Col sm={24} xs={24} md={24} lg={12} xl={12} className="pb-16">
                      <Radio.Button value={FamilyStatusEnum.MarriedWithDependents}>
                        {translate('ee-signup.family-status.married-with-dependents')}
                      </Radio.Button>
                    </Col>
                  </Row>
                </Radio.Group>
              </Col>
            </Row>
          </form>
        )
      }}
    </Formik>
  )
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateUserInStore: (user: User | null) => {
      dispatch(updateUserInStore(user))
    },
  }
}

export default connect(null, mapDispatchToProps)(FamilyStatus)
